﻿<script lang="ts" setup>
    import { inject, reactive, onMounted, watch, provide } from 'vue';
    import { setupInitValues } from './Contact';
    import { AccountAccess } from '@cwd/Account/Account.access';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { type Contact, loadContact, saveContactProperty } from './Contact.access';
    import { formatDateTime, formatDate } from '@cwd/Shared/utils/formatDateTime';
    import Form from '@cwd/shared/Components/Form/Form.vue';
    import { FormProps } from '@cwd/shared/components/Form/Form';
    import { vTooltip } from '@shared/directives';
    import { bsAlert } from '@shared/components/bsAlerts';
    import MultiSelect from '@shared/components/MultiSelect/MultiSelect.vue';
    import Tabs, { tab } from '@shared/components/Tabs.vue';
    import ContactFields from './ContactFields.vue';
    import ContactCustomFields from './ContactCustomFields.vue';
    import ContactLists from './ContactLists.vue';
    import ContactConsents from './ContactConsents.vue';
    import ContactDataGroups from './ContactDataGroups.vue';
    import ContactSubscriptions from './ContactSubscriptions.vue';
    import ContactTimeline from './ContactTimeline.vue';
    import Loading from '@shared/components/Loading.vue';
    import { loadLists, type List, loadCategories, type Category, type Timeline, loadTimeline, type DataGroup, loadDataGroups } from './Contact.access';
    import { setupTags } from '@shared/components/Tags/Tags';
    import { type TagsProvide } from '@shared/components/Tags/Tags.access';
    import { useRouter } from 'vue-router';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { FeatureService } from '@shared/directives/Feature.service';
    import get_gravatar from '@cwd/Navigation/Account/Gravatar.js';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const accountAccess: AccountAccess = injectorAccess.injector.get(AccountAccess);
    const featureAccess: FeatureService = injectorAccess.injector.get(FeatureService);
    const tags = setupTags(accountAccess.state.currentAccount?.AccountId);
    const router = useRouter();

    const emit = defineEmits<{
        (ev: 'goBack'): void;
    }>();

    provide<TagsProvide>('tags', {
        state: tags.state,
        create: tags.create,
        updateSelected: tags.updateSelected,
        toggle: tags.toggle,
    });

    const props = defineProps<{
        contactId: string | null;
        backBtn?: boolean;
    }>();

    const state = reactive<{
        isBusy: boolean;
        submitting: boolean;
        contact: Contact | null;
        formUnsubscribe: FormProps;
        lists: null | List[];
        consents: null | List[];
        categories: null | Category[];
        timeline: null | Timeline[];
        contactRemoved: boolean;
        hasScope: boolean;
        dataGroups: null | DataGroup[];
    }>({
        isBusy: false,
        submitting: false,
        contact: null,
        formUnsubscribe: {
            data: {
                IsActive: {
                    modelValue: undefined,
                    checkbox: {
                        label: $Resources.IsInactiveStatusLabel,
                        switchStyle: 'red-to-green',
                    },
                },
                IsSmsActive: {
                    modelValue: undefined,
                    checkbox: {
                        label: $Resources.IsSmsInactiveStatusLabel,
                        switchStyle: 'red-to-green',
                    },
                },
            },
        },
        lists: null,
        consents: null,
        categories: null,
        timeline: null,
        contactRemoved: false,
        hasScope: true,
        dataGroups: null,
    });

    let customField: any = null;

    onMounted(async () => {
        customField = accountAccess.state.currentAccount!.CustomField; // as any
        state.contactRemoved = false;
        props.contactId && setupContact(props.contactId);

        watch(
            () => props.contactId,
            async (newVal) => {
                state.contact = null; // reset
                state.timeline = null; // reset
                state.dataGroups = null; // reset
                state.contactRemoved = false;

                if (newVal === null) {
                    state.contactRemoved = true;
                } else {
                    setupContact(newVal);
                }
            }
        );
    });

    const setupContact = async (id: string) => {
        state.isBusy = true;
        state.contact = await loadContact(id);

        setTimeout(() => (state.isBusy = false), 0);

        setupInitValues(state.contact, state.formUnsubscribe);
    };

    const updateContactAndSave = (property: 'Lists' | 'Tags', newVal: any) => {
        state.contact![property] = newVal;
        onSaveContactProperty(property, newVal);
    };

    const printArea = () => {
        window.print();
    };

    const restoreEmailBounce = () => {
        state.contact!.HasBounced = false;
        onSaveContactProperty('HasBounced', false);
    };

    const restoreTextMessageBounce = () => {
        state.contact!.HasSmsBounced = false;
        onSaveContactProperty('HasSmsBounced', false);
    };

    let savingProperty: {
        [key: string]: boolean;
    } = {};

    const onSaveContactProperty = (property: string, newVal: any, updateContactState?: boolean) => {
        if (savingProperty[property]) return;
        savingProperty[property] = true;

        if (!state.contact) return;
        if (state.isBusy) return;

        state.submitting = true;

        saveContactProperty(state.contact.ContactId, property, newVal)
            .then((data) => {
                if (updateContactState) {
                    (state.contact! as any)[property] = newVal;
                }

                let content = '';

                if (property.match(/CustomNumeric(\d{1,2})/)) {
                    content = customField['CustomNumeric' + property.substring('CustomNumeric'.length)];
                } else {
                    content = property.match(/Custom(\d{1,2})/)
                        ? customField['CustomField' + property.substring('Custom'.length)]
                        : $Resources['PropertyContact' + property + 'Label'] || $Resources['propertyContact' + property + 'Label']; // ugly temp fix
                }

                bsAlert({
                    title: $Resources.PropertyUpdatedLabel,
                    content: content,
                });
            })
            .catch((error) => {
                let content = '';

                if (error.status === 400 && error.responseText.indexOf('Email exists') > 0) {
                    content = $Resources.PropertyContactEmailAlreadyExistsLabel;
                }
                if (property.match(/CustomNumeric(\d{1,2})/)) {
                    content = customField['CustomNumeric' + property.substring('CustomNumeric'.length)];
                } else {
                    content = property.match(/Custom(\d{1,2})/)
                        ? customField['CustomField' + property.substring('Custom'.length)]
                        : $Resources['PropertyContact' + property + 'Label'] || $Resources['propertyContact' + property + 'Label']; // ugly temp fix
                }

                bsAlert({
                    type: 'danger',
                    title: $Resources.PropertyNotUpdatedLabel,
                    content: content,
                });
            })
            .finally(() => {
                state.submitting = false;
                savingProperty[property] = false;
            });
    };

    const onLoadLists = () => {
        if (state.isBusy) return;
        if (state.lists !== null) return;
        state.isBusy = true;

        loadLists()
            .then((data: List[]) => {
                state.lists = data;
            })
            .catch(() => {
                bsAlert({ type: 'danger', title: $Resources.ErrorTitleLabel, content: $Resources.ErrorLabel });
            })
            .finally(() => {
                state.isBusy = false;
            });
    };

    const onLoadCategories = () => {
        if (state.isBusy) return;
        if (state.categories !== null) return;
        state.isBusy = true;

        loadCategories()
            .then((data: Category[]) => {
                state.categories = data;
            })
            .catch(() => {
                bsAlert({ type: 'danger', title: $Resources.ErrorTitleLabel, content: $Resources.ErrorLabel });
            })
            .finally(() => {
                state.isBusy = false;
            });
    };

    const onLoadTimeline = (force?: true) => {
        if (state.isBusy && !force) return;
        if (state.timeline !== null) return;
        state.isBusy = true;

        loadTimeline(state.contact!.ContactId)
            .then((data) => {
                state.timeline = data;
            })
            .catch(() => {
                bsAlert({ type: 'danger', title: $Resources.ErrorTitleLabel, content: $Resources.ErrorLabel });
            })
            .finally(() => {
                state.isBusy = false;
            });
    };

    const onLoadDataGroups = () => {
        if (state.isBusy) return;
        if (state.dataGroups !== null) return;
        state.isBusy = true;

        loadDataGroups(state.contact!.ContactId)
            .then((data) => {
                state.dataGroups = data;
            })
            .catch(() => {
                bsAlert({ type: 'danger', title: $Resources.ErrorTitleLabel, content: $Resources.ErrorLabel });
            })
            .finally(() => {
                state.isBusy = false;
            });
    };

    const backTo = () => {
        if (props.backBtn) return emit('goBack');

        router.currentRoute.value.query.returnTab
            ? router.push({
                  path: router.options.history.state.back as string,
                  query: { returnTab: router.currentRoute.value.query.returnTab },
              })
            : router.push(router.options.history.state.back as string);
    };

    const anonymized = () => {
        state.contact = null;
        state.contactRemoved = true;
    };

    const getPhotoUrl = () => {
        if (!state.contact) return '';
        if (state.contact.PhotoUrl) return state.contact.PhotoUrl;
        if (state.contact.Email && get_gravatar(state.contact.Email, 46)) return get_gravatar(state.contact.Email, 160);
    };
</script>

<script lang="ts"></script>

<template>
    <div class="contact-container">
        <div v-if="state.contact">
            <div class="m-lg-b fw-600" v-show="router.options.history.state.back || props.backBtn">
                <span class="glyphicon glyphicon-chevron-left m-xs-r"></span>
                <a href="javascript:void(0)" @click="backTo">{{ $Resources.BackButton }}</a>
            </div>

            <div class="contact-info d-flex flex-wrap col-gap-lg align-items-center">
                <div class="avatar-container m-0-t text-center">
                    <span class="profile-avatar d-flex round">
                        <span class="gravatar round" :style="{ 'background-image': 'url(' + getPhotoUrl() + ')' }"></span>
                    </span>

                    <span class="avatar-initials" v-if="state.contact.FirstName && state.contact.LastName">
                        <span>{{ state.contact.FirstName ? state.contact.FirstName.substring(0, 1) : '' }}</span>
                        <span>{{ state.contact.LastName ? state.contact.LastName.substring(0, 1) : '' }}</span>
                    </span>
                </div>

                <div class="flex-1 d-flex col-gap-lg">
                    <div class="flex-1 m-auto-r">
                        <div class="d-flex align-items-start">
                            <h2 class="m-0-t m-auto-r">
                                {{
                                    state.contact.FirstName || state.contact.LastName
                                        ? ((state.contact.FirstName || '') + ' ' + (state.contact.LastName || '')).trim()
                                        : $Resources.NoContactNamePlaceholder
                                }}
                            </h2>

                            <button type="button" v-tooltip.left="$Resources.PrintReportBtn" class="btn btn-default btn-sm hidden-print" @click="printArea">
                                <span class="glyphicon glyphicon-print"></span>
                            </button>
                        </div>

                        <div class="d-flex flex-wrap col-gap-lg m-sm-b">
                            <p v-show="state.contact.Email">
                                {{ state.contact.Email }}
                                <span class="unsubscribed-status" v-show="!state.contact.IsActive">
                                    <span class="glyphicon glyphicon-ban-circle m-xs-r"></span>
                                    <small>{{ $Resources.UnsubscribedStatus }}</small>
                                </span>
                                <span class="bounced-status" v-show="state.contact.HasBounced">
                                    <span class="glyphicon glyphicon-circle-arrow-left m-xs-r"></span>
                                    <small>
                                        {{ $Resources.EmailHasBouncedLabel }}
                                        <a href="javascript:void(0)" role="button" @click="restoreEmailBounce">
                                            {{ '(' + $Resources.RestoreContactEmailBounceButton + ')' }}
                                        </a>
                                    </small>
                                </span>
                            </p>

                            <p v-show="state.contact.SmsNumber">
                                {{ state.contact.SmsNumber }}
                                <span class="unsubscribed-status" v-show="!state.contact.IsSmsActive">
                                    <span class="glyphicon glyphicon-ban-circle m-xs-r"></span>
                                    <small>{{ $Resources.UnsubscribedStatus }}</small>
                                </span>
                                <span class="bounced-status" v-show="state.contact.HasSmsBounced">
                                    <span class="glyphicon glyphicon-circle-arrow-left m-xs-r"></span>
                                    <small>
                                        {{ $Resources.SmsNumberHasBouncedLabel }}
                                        <a href="javascript:void(0)" role="button" @click="restoreTextMessageBounce">
                                            {{ '(' + $Resources.RestoreContactSmsBounceButton + ')' }}
                                        </a>
                                    </small>
                                </span>
                            </p>

                            <p>
                                {{ $Resources.ContactCreatedLabel }}
                                <time :datetime="state.contact.Created">{{ formatDateTime(state.contact.Created) }}</time>
                            </p>
                        </div>

                        <Form
                            class="inputs-inline"
                            :data="state.formUnsubscribe.data"
                            :submitting="state.submitting"
                            :saveOnChange="true"
                            @update:modelValue="(key, newVal) => onSaveContactProperty(key, newVal, true)"></Form>
                    </div>

                    <div class="d-flex flex-direction-col m-sm-b">
                        <p class="contact-stat d-flex flex-direction-col">
                            <span class="label-look m-0-b">
                                NPS:
                                <button
                                    type="button"
                                    class="popover-trigger m-xs-l"
                                    v-popover-info.bottom="{
                                        heading: $Resources.ContactModalLastNpsRating,
                                        body: $Resources.NpsInfoText,
                                        displayHtml: true,
                                    }">
                                    <span class="glyphicon glyphicon-info-sign" aria-hidden="true"></span>
                                </button>
                            </span>
                            <span class="h3 m-xs-t m-xs-b" style="line-height: 1">
                                {{ state.contact.LastNpsRating || '-' }}
                                <span style="font-size: 50%; color: #999">/10</span>
                            </span>
                            <small v-show="state.contact.LastNpsRating">{{ formatDate(state.contact.LastNpsDate) }}</small>
                        </p>

                        <p class="contact-stat d-flex flex-direction-col">
                            <span class="label-look m-0-b">
                                CSAT:
                                <button
                                    type="button"
                                    class="popover-trigger m-xs-l"
                                    v-popover-info.bottom="{
                                        heading: $Resources.ContactLastCsatRating,
                                        body: $Resources.CsatInfoText,
                                        displayHtml: true,
                                    }">
                                    <span class="glyphicon glyphicon-info-sign" aria-hidden="true"></span>
                                </button>
                            </span>
                            <span class="h3 m-xs-t m-xs-b" style="line-height: 1">
                                {{ state.contact.LastCsatRating || '-' }}
                                <span style="font-size: 50%; color: #999">/10</span>
                            </span>
                            <small v-show="state.contact.LastCsatRating">{{ formatDate(state.contact.LastCsatDate) }}</small>
                        </p>
                    </div>
                </div>
            </div>

            <MultiSelect
                class="form-group"
                v-if="tags.state.list.length > 0"
                :loading="!tags.state.loaded"
                :items="tags.state.list"
                :selectedItems="state.contact.Tags"
                :label="$Resources.TagsLabel"
                :infoText="$Resources.TagsHelpText"
                displayProp="Title"
                selectProp="TagId"
                @update:selectedItems="(newVal) => updateContactAndSave('Tags', newVal)"
                @newItem="(newVal) => tags.create(newVal, state.contact!.Tags)"></MultiSelect>

            <tabs>
                <tab :title="$Resources.ContactModalProfileTab">
                    <ContactFields
                        :contact="state.contact"
                        :submitting="state.submitting"
                        @onSubmitSingleInput="(key: string, newVal: any) => onSaveContactProperty(key, newVal)"></ContactFields>
                </tab>
                <tab :title="$Resources.ContactModalCustomFieldsTab">
                    <ContactCustomFields
                        :contact="state.contact"
                        :submitting="state.submitting"
                        @onSubmitSingleInput="(key: string, newVal: any) => onSaveContactProperty(key, newVal)"></ContactCustomFields>
                </tab>
                <tab :title="$Resources.ContactModalActivityTab" @tab-click="onLoadTimeline()">
                    <ContactTimeline v-if="state.timeline" :contact="state.contact" :timeline="state.timeline" :isBusy="state.isBusy"></ContactTimeline>
                </tab>
                <tab :title="$Resources.ContactModalListsTab" @tab-click="onLoadLists()">
                    <ContactLists
                        v-if="state.lists"
                        :contact="state.contact"
                        :lists="state.lists"
                        :isBusy="state.isBusy"
                        @update:selectedItems="(newVal) => updateContactAndSave('Lists', newVal)"></ContactLists>
                </tab>
                <tab :title="$Resources.ContactModalCategoriesTab" @tab-click="onLoadCategories()">
                    <ContactSubscriptions
                        v-if="state.categories"
                        :contact="state.contact"
                        :categories="state.categories"
                        :submitting="state.submitting"
                        @update:modelValue="(newVal) => (state.contact!.Categories = newVal)"
                        @onSubmitSingleInput="onSaveContactProperty('Categories', state.contact!.Categories)"></ContactSubscriptions>
                </tab>
                <tab v-if="featureAccess.hasFeature('Contacts.DataPoints')" :title="$Resources.ContactModalDataPointTab" @tab-click="onLoadDataGroups()">
                    <ContactDataGroups v-if="state.dataGroups" :dataGroups="state.dataGroups"></ContactDataGroups>
                </tab>
                <tab :title="$Resources.ContactModalConsentTab">
                    <ContactConsents :contact="state.contact" :consents="state.contact.Consents" @anonymized="anonymized()"></ContactConsents>
                </tab>
            </tabs>
        </div>
        <div v-if="state.contactRemoved">
            <BsAlert
                type="warning"
                :title="$Resources.ContactModalContactRemovedAlertHeading"
                :content="$Resources.ContactModalContactRemovedAlertText"
                :dismissible="false"></BsAlert>
        </div>
    </div>

    <Loading :show="state.isBusy || state.submitting" :fixed="true"></Loading>
</template>

<style>
    .contact-container {
        margin: auto;
        padding: var(--ug-spacing-lg);
    }

    .contact-container .profile-avatar {
        width: 10rem;
        height: 10rem;
        border: 1px solid var(--border-color);
    }

    .modal-body .contact-container {
        max-width: unset;
        margin: 0;
        padding: 0;
    }
</style>
